import React from "react";

// CSRS gauge — half-donut SVG showing 0-100 score with color band
export default function CSRSGauge({ score = 0, band = "—", color = "rose", size = 280 }) {
  const radius = 100;
  const stroke = 18;
  const cx = 120;
  const cy = 120;
  // half circle from 180° to 360° (top half flipped)
  const startAngle = Math.PI; // 180°
  const endAngle = 2 * Math.PI; // 360°
  const arcLen = endAngle - startAngle;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const currentAngle = startAngle + arcLen * pct;

  const polar = (angle) => ({ x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) });
  const start = polar(startAngle);
  const end = polar(currentAngle);
  const endFull = polar(endAngle);
  const largeArc = currentAngle - startAngle > Math.PI ? 1 : 0;

  const colorMap = {
    rose: "#EF4444",
    amber: "#F59E0B",
    emerald: "#10B981",
  };
  const stroke_color = colorMap[color] || colorMap.amber;

  // Detect theme for text colors
  const isDark = typeof document !== "undefined" && document.documentElement.dataset.theme === "dark";
  const textColor = isDark ? "#F1F5F9" : "#0F172A";
  const trackColor = isDark ? "#1E293B" : "#E2E8F0";

  // Tick marks
  const ticks = [0, 25, 50, 75, 100].map(t => {
    const a = startAngle + arcLen * (t / 100);
    const inner = polar(a);
    const outerR = radius + 14;
    const outer = { x: cx + outerR * Math.cos(a), y: cy + outerR * Math.sin(a) };
    return { t, x1: inner.x, y1: inner.y, x2: outer.x, y2: outer.y };
  });

  return (
    <div className="inline-block" style={{ width: size }} data-testid="csrs-gauge">
      <svg viewBox="0 0 240 140" width={size} height={size * 0.65}>
        {/* Background arc */}
        <path
          d={`M ${start.x} ${start.y} A ${radius} ${radius} 0 1 1 ${endFull.x} ${endFull.y}`}
          stroke={trackColor}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
        />
        {/* Foreground arc */}
        {pct > 0 && (
          <path
            d={`M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`}
            stroke={stroke_color}
            strokeWidth={stroke}
            fill="none"
            strokeLinecap="round"
          />
        )}
        {/* Ticks */}
        {ticks.map(tk => (
          <g key={tk.t}>
            <line x1={tk.x1} y1={tk.y1} x2={tk.x2} y2={tk.y2} stroke="#94A3B8" strokeWidth={1.2} />
            <text x={tk.x2} y={tk.y2 - 4} fontSize="9" fill="#94A3B8" textAnchor="middle" fontFamily="IBM Plex Mono, monospace">{tk.t}</text>
          </g>
        ))}
        {/* Center text */}
        <text x={cx} y={cy - 20} textAnchor="middle" fontFamily="Cabinet Grotesk, sans-serif" fontWeight="800" fontSize="44" fill={textColor}>
          {score}
        </text>
        <text x={cx} y={cy} textAnchor="middle" fontSize="11" fill="#94A3B8" fontFamily="IBM Plex Mono, monospace" letterSpacing="2">
          /100
        </text>
        <text x={cx} y={cy + 22} textAnchor="middle" fontSize="13" fontWeight="600" fill={stroke_color}>
          {band}
        </text>
      </svg>
    </div>
  );
}
